'use client'

import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { useUserStats } from '@marketplace-web/shared/user-stats-data'
import { useUnreadMessagesContext } from '@marketplace-web/messaging/unread-messages-data'
import { useLocation } from '@marketplace-web/shared/browser'
import { useSession } from '@marketplace-web/shared/session'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  getInboxListItem,
  setConversationAsRead,
  InboxConversationListItemDto,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { HttpStatus, ResponseCode } from '@marketplace-web/core-api/api-client-util'

import ConversationListContext, { ConversationListContextType } from './ConversationListContext'
import useFetchInboxConversations from './useFetchInboxConversations'
import { DEFAULT_PAGE_NUMBER, DEFAULT_PER_PAGE, PER_PAGE_OPTIONS } from '../../constants'
import { getConversationIdFromUrl } from '../utils/conversation'
import { useConversationContext } from '../ConversationProvider'

type Props = {
  children: ReactNode
}

const ConversationListProvider = ({ children }: Props) => {
  const { conversation, conversationRequestStatus, conversationUiState } = useConversationContext()
  const { relativeUrl, searchParams: urlParams } = useLocation()
  const perPageParam = Number(urlParams.per_page)
  const perPage = PER_PAGE_OPTIONS.includes(perPageParam) ? perPageParam : DEFAULT_PER_PAGE

  const { user } = useSession()
  const userId = user?.id
  const unreadMsgCountEndpointSplitAbTest = useAbTest('unread_msg_count_endpoint_split')
  const isUnreadMsgCountEndpointSplitEnabled = unreadMsgCountEndpointSplitAbTest?.variant === 'on'

  const { userStats, setUnreadMsgCount: setUnreadMsgCountFromUserStats } = useUserStats()
  const {
    unreadMsgCount: unreadMsgCountFromConvoStats = 0,
    setUnreadMsgCount: setUnreadMsgCountFromConvoStats,
  } = useUnreadMessagesContext()

  const unreadConversationCount = isUnreadMsgCountEndpointSplitEnabled
    ? unreadMsgCountFromConvoStats
    : userStats?.unreadMsgCount ?? 0

  const setUnreadMsgCount = isUnreadMsgCountEndpointSplitEnabled
    ? setUnreadMsgCountFromConvoStats
    : setUnreadMsgCountFromUserStats

  const [currentPage, setCurrentPage] = useState(Number(urlParams.page) || DEFAULT_PAGE_NUMBER)

  const isConversationNotFound = useMemo(() => {
    const isError = !conversation && conversationUiState === UiState.Failure

    return isError && conversationRequestStatus === HttpStatus.NotFound
  }, [conversation, conversationRequestStatus, conversationUiState])

  const {
    uiState,
    controlConversations,
    conversations,
    pagination,
    isEndReached,
    setConversations,
    resetConversations,
    websocketUserId,
  } = useFetchInboxConversations({
    perPage,
    currentPage,
    setCurrentPage,
  })

  const addConversation = useCallback(
    (newConversation: InboxConversationListItemDto) => {
      setConversations(prevConversations => prevConversations.concat([newConversation]))
    },
    [setConversations],
  )

  const replaceConversation = useCallback(
    (newConversation: InboxConversationListItemDto) => {
      setConversations(prevConversations =>
        prevConversations.map(prevConversation =>
          String(prevConversation.id) === String(newConversation.id)
            ? newConversation
            : prevConversation,
        ),
      )
    },
    [setConversations],
  )

  const increaseUnreadCount = useCallback(() => {
    setUnreadMsgCount(unreadConversationCount + 1)
  }, [unreadConversationCount, setUnreadMsgCount])

  const decreaseUnreadCount = useCallback(() => {
    setUnreadMsgCount(Math.max(unreadConversationCount - 1, 0))
  }, [unreadConversationCount, setUnreadMsgCount])

  const isConversationIdInList = useCallback(
    (conversationId: string) => conversations.some(({ id }) => String(id) === conversationId),
    [conversations],
  )

  const updateConversation = useCallback<ConversationListContextType['updateConversation']>(
    async ({ id, title }) => {
      if (!userId) return

      const response = await getInboxListItem({ conversationId: id })

      if ('errors' in response) return

      const conversationIdFromUrl = getConversationIdFromUrl(relativeUrl)

      const listItem = response.conversation

      if (title) {
        listItem.description = title
      }

      if (listItem.unread) {
        if (String(listItem.id) === String(conversationIdFromUrl)) {
          listItem.unread = false
        } else {
          increaseUnreadCount()
        }
      }

      if (!listItem.updated_at) {
        listItem.updated_at = new Date(Date.now()).toISOString()
      }

      if (isConversationIdInList(String(id))) {
        replaceConversation(listItem)
      } else {
        addConversation(listItem)
      }
    },
    [
      addConversation,
      isConversationIdInList,
      increaseUnreadCount,
      relativeUrl,
      replaceConversation,
      userId,
    ],
  )

  const updateListConversationTitle = useCallback<
    ConversationListContextType['updateListConversationTitle']
  >(
    ({ id, title, updated_at = '' }) => {
      if (isConversationIdInList(String(id))) {
        setConversations(prevConversations =>
          prevConversations.map(prevConversation => {
            if (String(prevConversation.id) !== String(id)) return prevConversation

            return {
              ...prevConversation,
              subtitle: title,
              description: title,
              updated_at: updated_at || prevConversation.updated_at,
            }
          }),
        )
      } else {
        updateConversation({ id, title })
      }
    },
    [isConversationIdInList, setConversations, updateConversation],
  )

  const markAsRead = useCallback(
    async ({ id }) => {
      if (!userId) return

      const response = await setConversationAsRead({
        conversationId: id,
      })

      if (response.code !== ResponseCode.Ok) return

      setConversations(prevConversations =>
        prevConversations.map(prevConversation => {
          if (String(prevConversation.id) !== String(id) || !prevConversation.unread) {
            return prevConversation
          }

          decreaseUnreadCount()

          return {
            ...prevConversation,
            is_read_by_current_user: true,
            unread: false,
          }
        }),
      )
    },
    [decreaseUnreadCount, setConversations, userId],
  )

  const contextValues = useMemo<ConversationListContextType>(
    () => ({
      uiState,
      controlConversations,
      conversations: [...new Map(conversations.map(item => [item.id, item])).values()],
      pagination,
      isEndReached,
      perPage,
      currentPage,
      updateListConversationTitle,
      updateConversation,
      markAsRead,
      setCurrentPage,
      websocketUserId,
      unreadConversationCount,
    }),
    [
      uiState,
      controlConversations,
      conversations,
      pagination,
      isEndReached,
      perPage,
      currentPage,
      updateListConversationTitle,
      updateConversation,
      markAsRead,
      setCurrentPage,
      websocketUserId,
      unreadConversationCount,
    ],
  )

  useEffect(() => {
    if (isConversationNotFound) {
      resetConversations()
    }
  }, [isConversationNotFound, resetConversations])

  return (
    <ConversationListContext.Provider value={contextValues}>
      {children}
    </ConversationListContext.Provider>
  )
}

export default ConversationListProvider
